// Global command palette — opened with ⌘K / Ctrl+K or by clicking the topbar
// search trigger. Talks to /api/v1/search and renders grouped results with
// keyboard navigation. Lives once in the layout; the topbar emits a custom
// event ("cmdk:open") to open it.
//
// Markup contract: see app/views/shared/_command_palette.html.erb

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

const escapeHtml = (str) =>
  String(str ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));

const highlight = (text, query) => {
  const safe = escapeHtml(text);
  if (!query) return safe;
  const tokens = query.trim().split(/\s+/).filter((t) => t.length >= 2);
  if (tokens.length === 0) return safe;
  const pattern = new RegExp(`(${tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  return safe.replace(pattern, "<mark>$1</mark>");
};

class CommandPalette {
  constructor(root) {
    this.root = root;
    this.searchUrl = root.dataset.searchUrl;
    this.input = root.querySelector("[data-cmdk-input]");
    this.body = root.querySelector("[data-cmdk-body]");
    this.results = root.querySelector("[data-cmdk-results]");
    this.overlay = root.querySelector("[data-cmdk-overlay]");
    this.emptyQuery = root.querySelector("[data-cmdk-empty-query]");

    this.debounceTimer = null;
    this.activeAbort = null;
    this.activeIndex = -1;
    this.items = []; // flattened list of clickable result anchors

    this.bind();
  }

  bind() {
    this.overlay.addEventListener("click", () => this.close());
    this.input.addEventListener("input", () => this.scheduleSearch());
    this.input.addEventListener("keydown", (e) => this.onKeydown(e));
    this.results.addEventListener("mousemove", (e) => this.onMouseMove(e));
    this.results.addEventListener("click", (e) => this.onResultClick(e));
  }

  open(initialQuery = "") {
    if (!this.root.hasAttribute("hidden")) return;
    this.root.removeAttribute("hidden");
    this.root.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    if (initialQuery) {
      this.input.value = initialQuery;
      this.scheduleSearch();
    }
    requestAnimationFrame(() => this.input.focus());
  }

  close() {
    if (this.root.hasAttribute("hidden")) return;
    this.root.setAttribute("hidden", "");
    this.root.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    if (this.activeAbort) this.activeAbort.abort();
    clearTimeout(this.debounceTimer);
  }

  toggle() {
    this.root.hasAttribute("hidden") ? this.open() : this.close();
  }

  setState(state) { this.body.dataset.state = state; }

  scheduleSearch() {
    clearTimeout(this.debounceTimer);
    const query = this.input.value.trim();

    if (query.length < MIN_QUERY_LENGTH) {
      if (this.activeAbort) this.activeAbort.abort();
      this.results.innerHTML = "";
      this.items = [];
      this.activeIndex = -1;
      this.setState("idle");
      return;
    }

    this.setState("loading");
    this.debounceTimer = setTimeout(() => this.runSearch(query), DEBOUNCE_MS);
  }

  async runSearch(query) {
    if (this.activeAbort) this.activeAbort.abort();
    this.activeAbort = new AbortController();

    try {
      const url = `${this.searchUrl}?q=${encodeURIComponent(query)}`;
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.activeAbort.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      // Ignore stale responses if the input has changed.
      if (this.input.value.trim() !== query) return;
      this.render(data);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("[cmdk] search failed", err);
      this.setState("empty");
      if (this.emptyQuery) this.emptyQuery.textContent = query;
    }
  }

  render(data) {
    const groups = data.groups || [];
    if (groups.length === 0) {
      this.results.innerHTML = "";
      this.items = [];
      this.activeIndex = -1;
      this.setState("empty");
      if (this.emptyQuery) this.emptyQuery.textContent = data.query || this.input.value;
      return;
    }

    this.results.innerHTML = groups.map((group) => this.renderGroup(group, data.query)).join("");
    this.items = Array.from(this.results.querySelectorAll(".cmdk__item"));
    this.activeIndex = this.items.length > 0 ? 0 : -1;
    this.refreshActive();
    this.setState("results");
  }

  renderGroup(group, query) {
    const items = group.items.map((item) => this.renderItem(item, query)).join("");
    return `
      <section class="cmdk__group">
        <div class="cmdk__group-label">
          <i class="${escapeHtml(group.icon || "fa-solid fa-circle")}"></i>
          ${escapeHtml(group.label)}
        </div>
        ${items}
      </section>
    `;
  }

  renderItem(item, query) {
    const subtitle = item.subtitle
      ? `<span class="cmdk__item-subtitle">${highlight(item.subtitle, query)}</span>`
      : "";
    return `
      <a class="cmdk__item" href="${escapeHtml(item.url)}" data-cmdk-item>
        <span class="cmdk__item-icon"><i class="${escapeHtml(item.icon || "fa-solid fa-circle")}"></i></span>
        <span class="cmdk__item-content">
          <span class="cmdk__item-title">${highlight(item.title, query)}</span>
          ${subtitle}
        </span>
        <i class="fa-solid fa-arrow-right cmdk__item-arrow"></i>
      </a>
    `;
  }

  refreshActive() {
    this.items.forEach((el, idx) => el.classList.toggle("is-active", idx === this.activeIndex));
    if (this.activeIndex >= 0) {
      const active = this.items[this.activeIndex];
      active?.scrollIntoView({ block: "nearest" });
    }
  }

  moveActive(delta) {
    if (this.items.length === 0) return;
    this.activeIndex = (this.activeIndex + delta + this.items.length) % this.items.length;
    this.refreshActive();
  }

  onKeydown(event) {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        this.close();
        break;
      case "ArrowDown":
        event.preventDefault();
        this.moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.moveActive(-1);
        break;
      case "Enter":
        if (this.activeIndex >= 0) {
          event.preventDefault();
          this.activate(this.items[this.activeIndex]);
        }
        break;
    }
  }

  onMouseMove(event) {
    const item = event.target.closest(".cmdk__item");
    if (!item) return;
    const idx = this.items.indexOf(item);
    if (idx >= 0 && idx !== this.activeIndex) {
      this.activeIndex = idx;
      this.refreshActive();
    }
  }

  onResultClick(event) {
    const item = event.target.closest(".cmdk__item");
    if (!item) return;
    event.preventDefault();
    this.activate(item);
  }

  activate(item) {
    if (!item) return;
    this.close();
    // Use Turbo if available so we don't trigger a full page reload.
    if (window.Turbo?.visit) {
      window.Turbo.visit(item.href);
    } else {
      window.location.href = item.href;
    }
  }
}

let instance = null;

const initCommandPalette = () => {
  const root = document.getElementById("command-palette");
  if (!root) return;
  if (root.dataset.initialized === "true") {
    // Already wired in this Turbo navigation; just keep listening.
    return;
  }
  root.dataset.initialized = "true";
  instance = new CommandPalette(root);

  // Global ⌘K / Ctrl+K shortcut — also opens with the seed query if any.
  document.addEventListener("keydown", (event) => {
    if (!((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k")) return;
    event.preventDefault();
    instance.toggle();
  });

  // Topbar (or anything else) can request opening the palette.
  document.addEventListener("cmdk:open", (event) => {
    instance?.open(event.detail?.query ?? "");
  });
};

export { initCommandPalette };
